import Finance from '@carvana/ds-icons/FinanceSpot';
import TradeIn from '@carvana/ds-icons/TradeinSpot';
import { NavPayloadAction } from '@carvana/merch-native';
import breakpoints from '@carvana/merch-tailwind/breakpoints';
import cx from 'classnames';
import { type MouseEvent, useContext } from 'react';
import TrackWrapper from '~/components/common/TrackWrapper';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useIsMobile, useIsNative } from '~/context/common/DeviceContext';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import ModalContext from '~/context/home/ModalContext';
import useIsInShaqHomeTakeoverExperiment from '~/hooks/common/experiments/useIsInShaqHomeTakeoverExperiment';
import useTrackHeroPerformance from '~/hooks/home/telemetry/useTrackHeroPerformance';
import { useNativeOpenSoftPullOnClick } from '~/native/home/hooks';
import optimizeImage from '~/utilities/optimizeImage';
import Card from './Card';
import {
  CtaCardWrapper,
  HeroContentContainer,
  HeroContentWrapper,
  HeroImage,
  HeroTitle,
  HeroWrapper
} from './Hero.styles';
import Tiles from './Tiles';
import SearchBar from '../../common/SearchBar';

export default function Hero() {
  useTrackHeroPerformance();
  const { setSoftPullModalOpen } = useContext(ModalContext);
  const isMobile = useIsMobile();
  const isNative = useIsNative();
  const showComposerTreatment = useShowComposerTreatment();
  const isInShaqExperiment = useIsInShaqHomeTakeoverExperiment();
  const handleNativeOpenSoftPull = useNativeOpenSoftPullOnClick();

  const handleSoftPullClick = (ev: MouseEvent) => {
    handleNativeOpenSoftPull(ev);
    if (!isNative) setSoftPullModalOpen(true);
  };

  const viewEvent = {
    eventName: 'Home Feed Section',
    customAttributes: {
      action: 'viewed',
      section: 'Hero'
    }
  };

  const heroDesktopImage = isInShaqExperiment
    ? 'hero-shaq-desktop.jpg'
    : 'hero-desktop.jpg';
  const heroTabletImage = isInShaqExperiment
    ? 'hero-shaq-tablet.jpg'
    : 'hero-tablet.jpg';
  const heroMobileImage = isInShaqExperiment
    ? showComposerTreatment
      ? 'hero-shaq-mobile-frontier.jpg'
      : 'hero-shaq-mobile.jpg'
    : 'hero-mobile.jpg';
  const heroAlt = isInShaqExperiment
    ? "Shaquille O'Neal holding a phone displaying the Carvana app, standing near Carvana's car vending machine tower"
    : 'A Carvana hauler with a red car on it, driving through a neighborhood';

  const heroImg = {
    desktop: optimizeImage(
      `${appAssetsCdnUrl}/images/home/${heroDesktopImage}`,
      {
        height: isInShaqExperiment ? 1000 : 650,
        format: 'auto',
        optimize: isInShaqExperiment ? undefined : 'medium'
      }
    ),
    tablet: optimizeImage(`${appAssetsCdnUrl}/images/home/${heroTabletImage}`, {
      height: isInShaqExperiment ? 1000 : 600,
      format: 'auto',
      optimize: isInShaqExperiment ? undefined : 'medium'
    }),
    mobile: optimizeImage(`${appAssetsCdnUrl}/images/home/${heroMobileImage}`, {
      height: isInShaqExperiment ? 1000 : 550,
      format: 'auto',
      optimize: isInShaqExperiment ? undefined : 'high'
    })
  };

  return (
    <TrackWrapper event={viewEvent} trackOnce>
      <HeroWrapper>
        <HeroImage>
          <picture>
            <source
              media={`(min-width: ${breakpoints.md}px)`}
              srcSet={heroImg.desktop}
            />
            <source
              media={`(min-width: ${breakpoints.sm}px)`}
              srcSet={heroImg.tablet}
            />
            <img
              src={heroImg.mobile}
              alt={heroAlt}
              className={cx(
                'h-full w-full object-cover sm:object-[center_80%]',
                isInShaqExperiment
                  ? 'md:object-[40%_center] lg:object-center'
                  : 'md:object-center'
              )}
              fetchPriority="high"
            />
          </picture>
        </HeroImage>
        <HeroContentWrapper>
          <HeroContentContainer>
            <div className="flex flex-col gap-xl md:gap-2xl">
              <HeroTitle isInComposerTreatment={showComposerTreatment}>
                Right Car.
                <br />
                Right Price.
              </HeroTitle>
              {!showComposerTreatment && <SearchBar />}
            </div>
            {isMobile ? (
              <Tiles
                tiles={[
                  {
                    action: handleSoftPullClick,
                    icon: Finance,
                    nativePayload: {
                      action: NavPayloadAction.ViewTrades
                    },
                    text: 'Financing made easy',
                    testId: 'hero-finance-cta'
                  },
                  {
                    action: '/sell-my-car',
                    icon: TradeIn,
                    nativePayload: {
                      action: NavPayloadAction.ViewTrades
                    },
                    text: 'Sell or trade your car',
                    testId: 'hero-trade-cta'
                  }
                ]}
              />
            ) : (
              <CtaCardWrapper>
                <Card
                  action={handleSoftPullClick}
                  desktop={{
                    cta: 'Get Pre-Qualified',
                    heading: 'Financing made simple',
                    subHeading:
                      'No credit impact, view real payments while shopping'
                  }}
                  tablet={{
                    icon: Finance,
                    text: 'Finance with Carvana'
                  }}
                  testId="hero-finance-cta"
                />
                <Card
                  action="/sell-my-car"
                  desktop={{
                    cta: 'Get Your Offer',
                    heading: 'Sell or trade your car',
                    subHeading: 'Get a real offer in under 2 minutes'
                  }}
                  tablet={{
                    icon: TradeIn,
                    text: 'Sell or trade your car'
                  }}
                  testId="hero-trade-cta"
                />
              </CtaCardWrapper>
            )}
          </HeroContentContainer>
        </HeroContentWrapper>
      </HeroWrapper>
    </TrackWrapper>
  );
}
